import { useState, useEffect, useCallback } from "react";

import axios from "axios";

import { useOrgSession } from "@domains/auth/hooks/useOrgSession";
import { cacheManager } from "@platform/cache";
import { apiBaseUrls } from "@platform/constants/shared-constants";
import { useRegistryCache } from "@platform/hooks/useRegistryCache";

import type { PipelineStep } from "./pipeline-graph-from-state-helpers";

export interface UsePipelineRunStepsResult {
  steps: PipelineStep[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * usePipelineRunSteps — fetches PipelineStep[] for a specific run from the
 * REST endpoint `GET /applications/:alias/pipeline-runs/:sagaId/steps`.
 *
 * Unlike the bulk `/pipeline-state` endpoint (which returns all runs' steps
 * interleaved), this is scoped to exactly one sagaId — eliminating the
 * silent latest-run fallback that showed wrong-run data.
 *
 * The hook caches per organization, alias, and sagaId for 30 seconds. A
 * refetch explicitly invalidates the scoped entry (e.g. on SSE refresh).
 */
const PIPELINE_STEP_INVALIDATED = "rusha:pipeline-step-invalidated";

export function invalidatePipelineRunSteps(sagaId: string): void {
  cacheManager.invalidateByPrefix(`application:pipelineSteps:${sagaId}:`);
  if (typeof window !== "undefined") {
    window.dispatchEvent(
      new CustomEvent(PIPELINE_STEP_INVALIDATED, { detail: { sagaId } }),
    );
  }
}

export function usePipelineRunSteps(
  alias: string | undefined,
  sagaId: string | null,
): UsePipelineRunStepsResult & { refetch: () => void } {
  const [steps, setSteps] = useState<PipelineStep[] | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refetchSignal, setRefetchSignal] = useState(0);
  const { orgId } = useOrgSession();
  const { get: getCached, invalidate } = useRegistryCache("application");

  const fetchSteps = useCallback(async () => {
    if (!alias || !sagaId) {
      setSteps(null);
      return;
    }
    const cacheKey = `pipelineSteps:${sagaId}:${orgId ?? "anonymous"}:${alias}`;
    if (refetchSignal > 0) invalidate(cacheKey);
    setLoading(true);
    setError(null);
    try {
      const steps = await getCached(cacheKey, async () => {
        const res = await axios.get<PipelineStep[]>(
          `${apiBaseUrls.BACKEND}/api/v1/applications/${alias}/pipeline-runs/${sagaId}/steps`,
          { withCredentials: true },
        );
        return res.data;
      });
      setSteps(steps);
    } catch {
      setError("Failed to load pipeline steps for this run");
      setSteps([]);
    } finally {
      setLoading(false);
    }
  }, [alias, getCached, invalidate, orgId, refetchSignal, sagaId]);

  useEffect(() => {
    void fetchSteps();
  }, [fetchSteps]);

  useEffect(() => {
    const handler = (event: Event) => {
      const eventSagaId = (event as CustomEvent<{ sagaId?: string }>).detail
        ?.sagaId;
      if (eventSagaId === sagaId) {
        setRefetchSignal((n) => n + 1);
      }
    };
    window.addEventListener(PIPELINE_STEP_INVALIDATED, handler);
    return () => window.removeEventListener(PIPELINE_STEP_INVALIDATED, handler);
  }, [sagaId]);

  return {
    steps,
    loading,
    error,
    refetch: () => setRefetchSignal((n) => n + 1),
  };
}
